import { Pointer } from "rfc6902";
import { ControlTypeAhead, ControlTypeAheadProps } from "../../controls/ControlTypeAhead";
import { useARSContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { isUnlistedDatasetVariableValue } from "../utility/isUnlistedDatasetVariableValue";

/**
 * Props for the ARSVariable component.
 */
export interface ARSVariableProps extends Omit<
  ControlTypeAheadProps,
  "value" | "setValue" | "availableList"
> {
  /** RFC 6901 JSON Pointer path to the dataset field in ARSContext state */
  datasetDataPath: string;
  /** RFC 6901 JSON Pointer path to the variable field in ARSContext state */
  variableDataPath: string;
}

/**
 * A lightweight dataset/variable selector that reads from and writes to ARSContext state.
 * This is a thin wrapper around ControlTypeAhead that integrates with ARSContext.
 * Uses the variables list from ARSContext for typeahead functionality.
 *
 * When a variable is selected (e.g., "ADSL.AGE"), it automatically splits the value and updates
 * both the dataset field ("ADSL") and the variable field ("AGE") using a single dispatch with
 * multiple updates.
 *
 * @param props - Component props extending ControlTypeAheadProps.
 * @param props.datasetDataPath - RFC 6901 JSON Pointer path to the dataset field in ARSContext state.
 * @param props.variableDataPath - RFC 6901 JSON Pointer path to the variable field in ARSContext state.
 * @returns A React functional component rendering a ControlTypeAhead integrated with ARSContext.
 */
export const ARSVariable = ({
  datasetDataPath,
  variableDataPath,
  ...rest
}: ARSVariableProps): React.ReactElement => {
  const { state, dispatch } = useARSContext();

  // Get the current values from state using the dataPaths
  const datasetPointer = Pointer.fromJSON(datasetDataPath);
  const variablePointer = Pointer.fromJSON(variableDataPath);

  const dataset = (datasetPointer.get(state) as string) ?? "";
  const variable = (variablePointer.get(state) as string) ?? "";

  // Combine dataset and variable for display (e.g., "ADSL.AGE")
  const combinedValue = dataset && variable ? `${dataset}.${variable}` : dataset || variable || "";

  /**
   * Handle selection change by splitting the value and dispatching updates to both paths.
   * Expected format: "DATASET.VARIABLE" (e.g., "ADSL.AGE")
   *
   * @param newValue - The new combined value in format "DATASET.VARIABLE".
   */
  const handleChange = (newValue: string) => {
    // Split the value on the first period to separate dataset and variable
    const dotIndex = newValue.indexOf(".");
    let newDataset = "";
    let newVariable = "";

    if (dotIndex > 0) {
      newDataset = newValue.substring(0, dotIndex);
      newVariable = newValue.substring(dotIndex + 1);
    } else {
      // If no period found, treat entire value as dataset
      newDataset = newValue;
    }

    // Dispatch both updates atomically
    dispatch({
      operation: PATCH_ARS_STATE,
      updates: [
        { dataPath: datasetDataPath, value: newDataset },
        { dataPath: variableDataPath, value: newVariable },
      ],
    });
  };

  return (
    <ControlTypeAhead
      availableList={state.variables}
      value={combinedValue}
      setValue={handleChange}
      validCondition={isUnlistedDatasetVariableValue}
      {...rest}
    />
  );
};

ARSVariable.displayName = "ARSVariable";
